<template>
    <div
        class="sbs-file-preview-loader"
        :class="loaderClasses">
        <SbsStrokeLoader
            class="sbs-file-preview-loader__wrapper"
            v-bind="strokeLoaderData">
            <SbsButtonIcon
                class="sbs-file-preview-loader__file-image"
                v-bind="fileButtonData"
                v-on="fileButtonListeners">
                <span
                    class="sbs-file-preview-loader__file-image-bg  sbs-file-preview-loader__file-image-bg--static"
                    :class="fileBackgroundClasses">
                    <SbsIcon
                        class="sbs-file-preview-loader__file-image-icon  sbs-file-preview-loader__file-image-icon--file"
                        :iconName="fileIcon"
                        :colorTheme="fileIconTheme"
                        iconGroup="solid" />
                </span>

                <span
                    class="sbs-file-preview-loader__file-image-bg  sbs-file-preview-loader__file-image-bg--hover"
                    :class="fileHoverBackgroundClasses">
                    <SbsIcon
                        v-if="isDownloadIconVisible"
                        class="sbs-file-preview-loader__file-image-icon sbs-file-preview-loader__file-image-icon--download"
                        iconName="download"
                        :colorTheme="downloadIconTheme" />

                    <SbsIcon
                        v-else
                        class="sbs-file-preview-loader__file-image-icon  sbs-file-preview-loader__file-image-icon--cancel"
                        iconName="close"
                        iconSize="large"
                        :colorTheme="cancelIconTheme" />
                </span>
            </SbsButtonIcon>
        </SbsStrokeLoader>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';

const COMPLETED_PROGRESS = 100;

export default {
    name: 'SbsFilePreviewLoader',
    mixins: [themesMixin],
    data() {
        return {
            isLoaderFocused: false,
        };
    },
    computed: {
        loaderClasses() {
            return {
                disabled: this.disabled,
                focused: this.isLoaderFocused,
            };
        },
        isLoaded() {
            return this.progress >= COMPLETED_PROGRESS;
        },
        strokeLoaderData() {
            return {
                borderRadius: this.loaderBorderRadius,
                progress: this.progress,
                strokeTheme: this.isLoaded
                    ? AllColors.TRANSPARENT
                    : this.loaderStrokeTheme,
                strokeWidth: this.loaderStrokeWidth,
            };
        },
        fileButtonData() {
            const data = {
                disabled: this.disabled,
            };

            if (this.isLoaded && this.fileHref && !this.disabled) {
                data.href = this.fileHref;
                data.target = '_blank';

                if (this.downloadable) data.download = '';
            }

            return data;
        },
        fileButtonListeners() {
            return {
                ...this.$attrs,
                focus: this.fileButtonFocusHandler,
                blur: this.fileButtonBlurHandler,
            };
        },
        fileBackgroundClasses() {
            return this.fileBackgroundTheme
                ? `bg-${this.fileBackgroundTheme}`
                : '';
        },
        fileHoverBackgroundClasses() {
            return this.fileHoverBackground
                ? `bg-${this.fileHoverBackground}`
                : '';
        },
        isDownloadIconVisible() {
            return this.isLoaded && this.downloadable;
        },
    },
    methods: {
        fileButtonFocusHandler(e) {
            this.isLoaderFocused = true;
            this.$emit('focus', e);
        },
        fileButtonBlurHandler(e) {
            this.isLoaderFocused = false;
            this.$emit('blur', e);
        },
    },
    props: {
        /**
         * Цвет иконки удаления файла
         */
        cancelIconTheme: {
            type: String,
            default: AllColors.GRAY,
        },
        /**
         * Отключает любые действия с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность скачивания
         */
        downloadable: {
            type: Boolean,
            default: false,
        },
        /**
         * Цвет иконки загрузки/скачивания
         */
        downloadIconTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Цвет фона под иконкой файла
         */
        fileBackgroundTheme: {
            type: String,
            default: AllColors.GRADIENT_PALE_BLUE,
        },
        /**
         * Цвет фона иконки при наведении
         */
        fileHoverBackground: {
            type: String,
            default: AllColors.GRAY_ULTRA_LIGHT,
        },
        /**
         * Ссылка на файл
         */
        fileHref: {
            type: String,
            default: '',
        },
        /**
         * Иконка файла
         */
        fileIcon: {
            type: String,
            default: 'file',
        },
        /**
         * Цвет иконки файла
         */
        fileIconTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Радиус скругления
         */
        loaderBorderRadius: {
            type: Number,
            default: 10,
        },
        /**
         * Цвет полосы загрузки
         */
        loaderStrokeTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Толщина полосы загрузки
         */
        loaderStrokeWidth: {
            type: Number,
            default: 2,
        },
        /**
         * Прогресс загрузки файла
         */
        progress: {
            type: Number,
            default: 0,
        },
    },
};
</script>

<style lang="scss">
.sbs-file-preview-loader {
    display: flex;

    &:not(:active):not(.disabled).focused {
        outline: 1px auto $current-color;
        outline-offset: 3px;
    }

    &__wrapper {
        width: 55px;
        height: 55px;
    }

    &__file-image {
        position: relative;
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    &__file-image-bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: $trs;

        &--hover {
            opacity: 0;

            @include has-hover {
                .sbs-file-preview-loader .sbs-file-preview-loader__file-image:hover & {
                    opacity: 1;
                }
            }

            .sbs-file-preview-loader .sbs-file-preview-loader__file-image:focus & {
                opacity: 1;
            }
        }

        &--static {
            @include has-hover {
                .sbs-file-preview-loader .sbs-file-preview-loader__file-image:hover & {
                    opacity: 0;
                }
            }

            .sbs-file-preview-loader .sbs-file-preview-loader__file-image:focus & {
                opacity: 0;
            }
        }
    }

    &__file-image-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 5;
        transform: translate(-50%, -50%) scale(0) rotate(0);
        transition: $trs;

        &--cancel {
            opacity: 0;

            @include has-hover {
                .sbs-file-preview-loader__file-image:hover & {
                    .sbs-file-preview-loader & {
                        transform: translate(-50%, -50%) scale(1) rotate(180deg);
                        opacity: 1;
                    }
                }
            }

            .sbs-file-preview-loader__file-image:focus & {
                .sbs-file-preview-loader & {
                    transform: translate(-50%, -50%) scale(1) rotate(180deg);
                    opacity: 1;
                }
            }
        }

        &--download {
            opacity: 0;

            @include has-hover {
                .sbs-file-preview-loader__file-image:hover & {
                    .sbs-file-preview-loader & {
                        transform: translate(-50%, -50%) scale(1);
                        opacity: 1;
                    }
                }
            }

            .sbs-file-preview-loader__file-image:focus & {
                .sbs-file-preview-loader & {
                    transform: translate(-50%, -50%) scale(1);
                    opacity: 1;
                }
            }
        }

        &--file {
            transform: translate(-50%, -50%) scale(1) rotate(0);

            @include has-hover {
                .sbs-file-preview-loader .sbs-file-preview-loader__file-image:hover & {
                    transform: translate(-50%, -50%) scale(0) rotate(0);
                    opacity: 0;
                }
            }

            .sbs-file-preview-loader .sbs-file-preview-loader__file-image:focus & {
                transform: translate(-50%, -50%) scale(0) rotate(0);
                opacity: 0;
            }
        }
    }
}
</style>
